/**
 * dwc-wheel-diameter-slider.js
 *
 * Progressive enhancement that turns discrete numeric metafield checkbox filters (e.g.
 * "Wheel Diameter", "Maximum Capacity") into dual-handle sliders with editable number inputs,
 * styled to match the theme's own Price Range filter.
 *
 * This does NOT modify any of the theme's original filter files. Instead, once Shopify has
 * rendered the normal checkbox-list filter for a given metafield, this script finds that
 * checkbox list, visually hides it, and builds a slider in its place. Moving the slider
 * checks/unchecks the real (hidden) checkboxes, so the theme's existing facet-filters.js
 * keeps handling the actual filtering, URL updates, and AJAX result refresh exactly as it
 * already does for every other filter.
 *
 * Because facet-filters.js replaces the whole filter form's HTML from the server on every
 * filter change (see applyFilters() in facet-filters.js), this script re-runs every time that
 * happens (`on:facet-filters:updated`) as well as on first load and inside the theme editor.
 *
 * To add another numeric metafield filter, just add another entry to FILTERS below.
 */
(function () {
  var FILTERS = [
    { match: 'specification.wheel_diameter_mm', label: 'Wheel Diameter', unit: 'mm' },
    { match: 'specification.maximum_capacity_kg', label: 'Maximum Capacity', unit: 'kg' },
    { match: 'specification.tread_width_mm', label: 'Tread Width', unit: 'mm' },
    { match: 'specification.overall_height_mm', label: 'Overall Height', unit: 'mm' },
    { match: 'specification.unsleeved_hub_length_mm', label: 'Unsleeved Hub Length', unit: 'mm' },
    { match: 'specification.sleeved_hub_length_mm', label: 'Sleeved Hub Length', unit: 'mm' },
    { match: 'specification.unsleeved_bore_diameter', label: 'Unsleeved Bore Diameter', unit: 'mm' },
    { match: 'specification.sleeved_bore_diameter', label: 'Sleeved Bore Diameter', unit: 'mm' }
  ];

  // Filters that should start collapsed, matched by their visible label text. This only runs
  // on a genuinely fresh page load (and inside the theme editor), never after an actual filter
  // change, so it never fights with a filter the person has just opened or closed themselves -
  // the theme's own facet-filters.js already preserves open/closed state across AJAX updates.
  var DEFAULT_COLLAPSED_LABELS = ['Series', 'High Temperature Rated', 'Low Temperature Rated', 'Sleeved Hub Length', 'Unsleeved Hub Length', 'Sleeved Bore Diameter', 'Unsleeved Bore Diameter','Brand', 'Wheel Material', 'Tread Width', 'Price Range'];

  /**
   * Collapses any filter whose visible label matches DEFAULT_COLLAPSED_LABELS.
   */
  function applyDefaultCollapsedFilters() {
    var allDetails = document.querySelectorAll('.facets__filters details-disclosure > details');

    allDetails.forEach(function (details) {
      var labelEl = details.querySelector('.filter__toggle .flex-auto');
      if (!labelEl) return;

      var label = labelEl.textContent.trim();
      if (DEFAULT_COLLAPSED_LABELS.indexOf(label) !== -1) {
        details.open = false;
      }
    });
  }

  /**
   * Hides an entire filter's disclosure block (used when a filter has fewer than 2 possible
   * values, making it useless for actual filtering, and not worth showing at all).
   * @param {Element} list - The <ul> of checkboxes for the filter.
   */
  function hideFilter(list) {
    var wrapper = list.closest('details-disclosure');
    if (wrapper) wrapper.hidden = true;
  }

  /**
   * Finds the checkbox list for a given filter config and builds a slider on top of it.
   * @param {Object} config - One entry from FILTERS ({match, label, unit}).
   */
  function enhanceFilter(config) {
    var checkboxes = Array.prototype.slice.call(
      document.querySelectorAll('input[type="checkbox"][name*="' + config.match + '"]')
    );

    if (checkboxes.length === 0) return;

    var list = checkboxes[0].closest('ul[role="list"]');
    if (!list) return;

    // Only one possible value on this collection - nothing to actually filter, and left alone
    // it would just show Shopify's raw single checkbox with the unformatted decimal value
    // (e.g. "62.0"). Hide the whole filter instead.
    if (checkboxes.length < 2) {
      hideFilter(list);
      return;
    }

    if (list.dataset.dwcSliderApplied === 'true') return;
    list.dataset.dwcSliderApplied = 'true';

    // Build a sorted, de-duped array of {checkbox, value, label} from the real filter values.
    var steps = checkboxes
      .map(function (checkbox) {
        var label = checkbox.closest('li').querySelector('.flex-auto');
        return {
          checkbox: checkbox,
          value: parseFloat(checkbox.value),
          label: label ? label.textContent.trim() : checkbox.value
        };
      })
      .filter(function (step) {
        return !isNaN(step.value);
      })
      .sort(function (a, b) {
        return a.value - b.value;
      });

    if (steps.length < 2) {
      hideFilter(list);
      return;
    }

    var lastIndex = steps.length - 1;

    // Restore slider position from whichever checkboxes are already checked
    // (e.g. after a full page load from a shared/back-button URL).
    var activeIndexes = [];
    steps.forEach(function (step, index) {
      if (step.checkbox.checked) activeIndexes.push(index);
    });
    var startMin = activeIndexes.length ? Math.min.apply(null, activeIndexes) : 0;
    var startMax = activeIndexes.length ? Math.max.apply(null, activeIndexes) : lastIndex;

    var minValue = steps[0].value;
    var maxValue = steps[lastIndex].value;
    var hasActiveFilter = activeIndexes.length > 0;
    var unit = config.unit;

    // Build the slider markup - number inputs + range track, matching the theme's
    // existing Price Range filter layout but with a unit (mm/kg/etc.) instead of a currency
    // symbol. Like the Price Range inputs, the default (no filter applied) state shows the
    // min/max as a placeholder rather than a real value, only becoming a real value once a
    // range is actually selected.
    var wrapper = document.createElement('div');
    wrapper.className = 'dwc-wheel-diameter-slider';
    wrapper.innerHTML =
      '<div class="flex items-center">' +
        '<div class="dwc-wheel-diameter-slider__input-wrapper flex items-center flex-auto relative">' +
          '<input type="number" class="input" data-role="min-number" placeholder="' + minValue + '"' +
            (hasActiveFilter ? ' value="' + steps[startMin].value + '"' : '') +
            ' min="' + minValue + '" max="' + maxValue + '" step="1">' +
          '<span class="dwc-wheel-diameter-slider__unit">' + unit + '</span>' +
        '</div>' +
        '<span class="dwc-wheel-diameter-slider__input-separator">-</span>' +
        '<div class="dwc-wheel-diameter-slider__input-wrapper flex items-center flex-auto relative">' +
          '<input type="number" class="input" data-role="max-number" placeholder="' + maxValue + '"' +
            (hasActiveFilter ? ' value="' + steps[startMax].value + '"' : '') +
            ' min="' + minValue + '" max="' + maxValue + '" step="1">' +
          '<span class="dwc-wheel-diameter-slider__unit">' + unit + '</span>' +
        '</div>' +
      '</div>' +
      '<div class="dwc-wheel-diameter-slider__track relative">' +
        '<input type="range" class="dwc-wheel-diameter-slider__input" data-role="min-slider" min="0" max="' + lastIndex + '" step="1" value="' + startMin + '" tabindex="-1">' +
        '<input type="range" class="dwc-wheel-diameter-slider__input" data-role="max-slider" min="0" max="' + lastIndex + '" step="1" value="' + startMax + '" tabindex="-1">' +
      '</div>';

    list.insertAdjacentElement('beforebegin', wrapper);
    list.classList.add('dwc-visually-hidden');

    // The "Show more" button just reveals more checkboxes inside the now-hidden list, which
    // has no visible effect once the slider is in charge, so remove it. Also relabel "Clear"
    // to "Reset" for this filter, since a slider doesn't really have anything to "clear".
    var panel = list.closest('.disclosure__content');
    if (panel) {
      var showMoreBtn = panel.querySelector('.js-show-more');
      if (showMoreBtn) showMoreBtn.remove();

      var clearLink = panel.querySelector('.js-clear-filter');
      if (clearLink) clearLink.textContent = 'Reset';
    }

    var minSlider = wrapper.querySelector('[data-role="min-slider"]');
    var maxSlider = wrapper.querySelector('[data-role="max-slider"]');
    var minNumber = wrapper.querySelector('[data-role="min-number"]');
    var maxNumber = wrapper.querySelector('[data-role="max-number"]');

    /**
     * Finds the index of the step whose value is closest to a given number, so typed values
     * that don't exactly match a real value snap to the nearest one that does.
     */
    function indexForValue(value) {
      var closestIndex = 0;
      var closestDiff = Infinity;
      steps.forEach(function (step, index) {
        var diff = Math.abs(step.value - value);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = index;
        }
      });
      return closestIndex;
    }

    function setIndexes(minIndex, maxIndex) {
      if (minIndex > maxIndex) {
        var temp = minIndex;
        minIndex = maxIndex;
        maxIndex = temp;
      }

      minSlider.value = minIndex;
      maxSlider.value = maxIndex;
      // Match price-range.js: show a real value once moved off the extreme, otherwise
      // clear it so the placeholder (the true min/max) shows through again.
      minNumber.value = minIndex !== 0 ? steps[minIndex].value : '';
      maxNumber.value = maxIndex !== lastIndex ? steps[maxIndex].value : '';
    }

    function updateCheckboxes() {
      var minIndex = Number(minSlider.value);
      var maxIndex = Number(maxSlider.value);
      var isFullRange = minIndex === 0 && maxIndex === lastIndex;
      var changed = false;

      steps.forEach(function (step, index) {
        var shouldCheck = !isFullRange && index >= minIndex && index <= maxIndex;
        if (step.checkbox.checked !== shouldCheck) {
          step.checkbox.checked = shouldCheck;
          changed = true;
        }
      });

      if (changed) {
        // Bubbles up to facet-filters.js's listener on `.facets__filters`, which reads the
        // whole form via FormData, so this is all it needs to trigger real filtering.
        wrapper.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    // Dragging the sliders: update instantly for a smooth drag, but debounce the actual
    // checkbox/filter update so it only fires once dragging settles, rather than on every
    // pixel of movement.
    var filterUpdateTimeout = null;
    function scheduleFilterUpdate() {
      clearTimeout(filterUpdateTimeout);
      filterUpdateTimeout = setTimeout(updateCheckboxes, 300);
    }

    minSlider.addEventListener('input', function () {
      setIndexes(Number(minSlider.value), Number(maxSlider.value));
      scheduleFilterUpdate();
    });

    maxSlider.addEventListener('input', function () {
      setIndexes(Number(minSlider.value), Number(maxSlider.value));
      scheduleFilterUpdate();
    });

    // Typing in the number fields: debounce so the filter only updates once typing pauses,
    // same behaviour as the theme's own Price Range inputs.
    var typingTimeout = null;
    function handleNumberInput() {
      clearTimeout(typingTimeout);
      typingTimeout = setTimeout(function () {
        var minRaw = minNumber.value.trim();
        var maxRaw = maxNumber.value.trim();
        var minIndex = minRaw === '' ? 0 : indexForValue(parseFloat(minRaw));
        var maxIndex = maxRaw === '' ? lastIndex : indexForValue(parseFloat(maxRaw));
        setIndexes(minIndex, maxIndex);
        updateCheckboxes();
      }, 600);
    }

    minNumber.addEventListener('input', handleNumberInput);
    maxNumber.addEventListener('input', handleNumberInput);

    consolidateActiveFilterChips(steps, config);
  }

  /**
   * Shopify's "active filters" summary at the top of the drawer shows one removable chip per
   * selected checkbox value. Selecting a wide slider range can select many discrete values at
   * once, which would otherwise explode into a wall of chips. This collapses all of this
   * filter's active chips into a single "<Label>: X <unit> - Y <unit>" chip with a working
   * clear button, without touching the theme's original active-filters markup/logic for any
   * other filter.
   * @param {Array} steps - The sorted {checkbox, value, label} steps built in enhanceFilter().
   * @param {Object} config - The FILTERS entry this filter belongs to ({match, label, unit}).
   */
  function consolidateActiveFilterChips(steps, config) {
    var activeFiltersList = document.querySelector('.active-filters');
    if (!activeFiltersList) return;

    var activeSteps = steps.filter(function (step) {
      return step.checkbox.checked;
    });
    if (activeSteps.length < 2) return;

    var activeLabels = activeSteps.map(function (step) {
      return step.label;
    });

    var chipsToRemove = Array.prototype.filter.call(
      activeFiltersList.querySelectorAll('.active-filter'),
      function (chip) {
        return activeLabels.indexOf(chip.textContent.trim()) !== -1;
      }
    );

    // If the values shown don't line up 1:1 with what we expect, leave it alone rather than
    // risk hiding/removing chips that belong to a different filter with an overlapping label.
    if (chipsToRemove.length !== activeSteps.length) return;

    var icon = chipsToRemove[0].querySelector('svg');
    var combinedChip = document.createElement('a');
    combinedChip.href = '#';
    combinedChip.className = 'active-filter flex items-center text-current';
    if (icon) combinedChip.appendChild(icon.cloneNode(true));
    combinedChip.appendChild(document.createTextNode(
      config.label + ': ' + activeSteps[0].label + ' ' + config.unit + ' - ' +
      activeSteps[activeSteps.length - 1].label + ' ' + config.unit
    ));

    combinedChip.addEventListener('click', function (evt) {
      evt.preventDefault();
      evt.stopPropagation();

      var changed = false;
      steps.forEach(function (step) {
        if (step.checkbox.checked) {
          step.checkbox.checked = false;
          changed = true;
        }
      });

      // Dispatch from a checkbox (inside .facets__filters) rather than the chip itself
      // (inside .facets__active-filters) so the event bubbles to where facet-filters.js
      // is actually listening.
      if (changed) steps[0].checkbox.dispatchEvent(new Event('input', { bubbles: true }));
    });

    chipsToRemove[0].insertAdjacentElement('beforebegin', combinedChip);
    chipsToRemove.forEach(function (chip) {
      chip.remove();
    });

    // Shopify's own count (e.g. "16 Applied Filters") counts every underlying checkbox.
    // Reduce it by the number of chips we just collapsed into one, so it reads "1" for this
    // filter instead of 16. This just edits the number inside the existing translated string,
    // so it won't get pluralisation exactly right in every locale, but it's close enough and
    // avoids re-implementing Shopify's translation logic in JS.
    var header = document.querySelector('.active-filters-header');
    if (header) {
      var match = header.textContent.match(/\d+/);
      if (match) {
        var adjustedCount = Math.max(1, parseInt(match[0], 10) - (chipsToRemove.length - 1));
        header.textContent = header.textContent.replace(/\d+/, adjustedCount);
      }
    }
  }

  function enhance() {
    FILTERS.forEach(enhanceFilter);
  }

  function init() {
    enhance();
    applyDefaultCollapsedFilters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run after the theme's AJAX filter update replaces the filter form's HTML.
  // Deliberately does NOT re-run applyDefaultCollapsedFilters() here, since the theme's own
  // facet-filters.js already preserves each filter's current open/closed state across these
  // updates, and re-forcing the default would undo anything the person just opened.
  document.addEventListener('on:facet-filters:updated', enhance);

  // Re-run when a merchant edits sections inside the theme editor.
  document.addEventListener('shopify:section:load', function () {
    enhance();
    applyDefaultCollapsedFilters();
  });
})();